import Autocomplete from "@mui/material/Autocomplete";
import Icon from "@mui/material/Icon";

import { useEffect, useMemo } from "react";

import Helpers from "../../../commons/helpers";
import useStrings from "../../../hooks/useStrings";
import Box from "../../Box";
import TextField from "../../TextField";
import Typography from "../../Typography";
import Pagination from "../Pagination";

import { DataTableProps } from "..";
import { Constants } from "../../../constants/constants";

let defaultValue = 0;

export const TableFooter = <T,>({
    totalCount,
    pageNumber,
    rowPerPage,
    rowPerPageOptions,
    onChangePageSize,
    onChangePageNumber,
    pagination,
    canPreviousPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageIndex,
    pageSize,
    defaultValue,
}: Pick<
    DataTableProps<T>,
    "totalCount" | "pageNumber" | "rowPerPage" | "rowPerPageOptions" | "onChangePageSize" | "onChangePageNumber" | "pagination"
> & {
    canPreviousPage: boolean;
    gotoPage: (updater: number | ((pageIndex: number) => number)) => void;
    nextPage: () => void;
    previousPage: () => void;
    setPageSize: (pageSize: number) => void;
    pageIndex: number;
    pageSize: number;
    defaultValue: number;
}): JSX.Element => {
    const strings = useStrings();
    let entries: number[] = rowPerPageOptions || Constants.ROW_PER_PAGE_OPTIONS;

    let dataPageOptions: number[] = [];
    for (let index = 0; index < Math.ceil(totalCount / defaultValue); index++) {
        dataPageOptions.push(index);
    }

    const tablePageSizeOptions = useMemo(() => {
        let isPageSizedIncluded = false;

        entries.forEach((item, itemIndex) => {
            if (pageSize === item) {
                isPageSizedIncluded = true;
            }
        });

        // Included cause not item small than it
        if (isPageSizedIncluded) {
            return entries;
        } else {
            return entries.sort((a, b) => a - b);
        }
    }, [pageSize, entries]);

    // Set the default value for the entries per page when component mounts
    useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

    // Setting the entries starting point, entries ending point
    let entriesStart = 0;
    let entriesEnd = 0;
    if (pageIndex + 1 <= Math.ceil(totalCount / defaultValue) && pageIndex >= 0) {
        entriesEnd = Math.min((pageIndex + 1) * defaultValue, totalCount);
        entriesStart = Math.min(pageIndex * defaultValue + 1, entriesEnd);
    }
    let canNextPage = entriesEnd < totalCount;

    // Set the entries per page value based on the select value
    const setEntriesPerPage = (value: any) => setPageSize(value);

    // Render the paginations
    const renderPagination = dataPageOptions.map((option: any) => (
        <Pagination
            item
            key={option}
            active={pageIndex === option}
            onClick={() => {
                gotoPage(Number(option));
                Helpers.isFunction(onChangePageNumber) && onChangePageNumber(Number(option) + 1);
            }}
        >
            {option + 1}
        </Pagination>
    ));

    // Handler for the input to set the pagination index
    const handleInputPagination = ({ target: { value } }: any) =>
        value > dataPageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

    // Customized page options starting from 1
    const customizedPageOptions = dataPageOptions.map((option: any) => option + 1);

    // Setting value for the pagination input
    const handleInputPaginationValue = ({ target: value }: any) => gotoPage(Number(value.value - 1));

    return (
        <Box
            py={2}
            px={{ xs: 0, sm: 2 }}
            display="flex"
            justifyContent="end"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
        >
            {/* Page Size */}
            <Box>
                <Typography variant="button" color="secondary" fontWeight="regular">
                    {strings.Common.DISPLAY}:
                </Typography>
                <Autocomplete
                    size="small"
                    disableClearable
                    key={defaultValue}
                    value={defaultValue}
                    options={tablePageSizeOptions}
                    sx={{ width: "70px", margin: "0px 10px", display: "inline-flex" }}
                    getOptionLabel={(option: any) => `${option}`}
                    renderInput={(params) => <TextField {...params} variant="outlined" />}
                    onChange={(event, newValue) => {
                        setEntriesPerPage(newValue);
                        Helpers.isFunction(onChangePageSize) && onChangePageSize(newValue);
                    }}
                />
                <Typography variant="button" color="secondary" fontWeight="regular">
                    {strings.format(strings.Common.LABEL_DISPLAY_ROWS, entriesStart || 0, entriesEnd || 0, totalCount || 0)}
                </Typography>
            </Box>
            {/* Pages Number */}
            {dataPageOptions.length > 1 && (
                <Box mt={{ xs: 1, sm: 0 }} ml={{ xs: 0, sm: 1 }}>
                    <Pagination variant={pagination?.variant || "gradient"} color={pagination?.color || "info"}>
                        {canPreviousPage && (
                            <Pagination
                                item
                                onClick={() => {
                                    previousPage();
                                    Helpers.isFunction(onChangePageNumber) && onChangePageNumber(pageNumber - 1);
                                }}
                            >
                                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                            </Pagination>
                        )}
                        {renderPagination.length > 5 ? (
                            <Box width="3rem" mx={1}>
                                <TextField
                                    value={customizedPageOptions[pageIndex]}
                                    inputProps={{
                                        type: "number",
                                        min: 1,
                                        max: customizedPageOptions.length,
                                    }}
                                    onChange={(event: any) => {
                                        handleInputPagination(event);
                                        handleInputPaginationValue(event);
                                        Helpers.isFunction(onChangePageNumber) && onChangePageNumber(Number(event.target.value));
                                    }}
                                />
                            </Box>
                        ) : (
                            renderPagination
                        )}
                        {canNextPage && (
                            <Pagination
                                item
                                onClick={() => {
                                    nextPage();
                                    Helpers.isFunction(onChangePageNumber) && onChangePageNumber(pageNumber + 1);
                                }}
                            >
                                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                            </Pagination>
                        )}
                    </Pagination>
                </Box>
            )}
        </Box>
    );
};

export default TableFooter;
